// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Map as MapIcon,
  Plus,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import SortableHeader from "../../components/admin/SortableHeader";
import PageLoader from "../../components/PageLoader";
import locationService from "../../services/locationService";
import scenarioService from "../../services/scenarioService";
import tenantService from "../../services/tenantService";
import { formatErrorMessage } from "../../utils/apiHelper";
import { EVENT_TYPE_MAP, EVENT_SIZE_MAP } from "../../utils/mappings";
import useSortableData from "../../hooks/useSortableData";
import { useRoutes } from "../../hooks/useRoutes";
import {
  DEFAULT_VIEWBOX,
  parseViewBoxString,
  formatViewBoxString,
} from "../../utils/scenarioEditorUtils";
import {
  hasSuperAdminAccess,
  hasMapEditorAccess,
} from "../../config/roleConfig";

const DEFAULT_PAGE_SIZE = 25;

const SCENARIO_SORT_ACCESSORS = {
  id: (s) => Number(s.id) || 0,
  status: (s) => (s.is_active ? 1 : 0),
  event_type: (s) => EVENT_TYPE_MAP[s.event_type] || s.event_type || "",
  event_size: (s) => EVENT_SIZE_MAP[s.event_size] || s.event_size || "",
  active_period: (s) => s.active_from || s.active_until || "",
};

const DEFAULT_SCENARIO_SORT = { key: "id", direction: "asc" };

const getScenarioWindowState = (scenario) => {
  const now = Date.now();
  const until = scenario.active_until
    ? new Date(scenario.active_until).getTime()
    : null;
  const from = scenario.active_from
    ? new Date(scenario.active_from).getTime()
    : null;
  if (until != null && !Number.isNaN(until) && until < now) return "expired";
  if (from != null && !Number.isNaN(from) && from > now) return "scheduled";
  return "live";
};

const toDateInputValue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toIsoFromDateInput = (value, endOfDay = false) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const d = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const normalizeScenarioFromApi = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id ?? raw.scenario_id;
  if (id == null) return null;

  return {
    id: String(id),
    location_id: Number(raw.location_id) || 0,
    name: raw.name ?? "",
    scenario_code: raw.scenario_code ?? "",
    description: raw.description ?? "",
    reference_lat: raw.reference_lat ?? "",
    reference_lng: raw.reference_lng ?? "",
    area_sqm: raw.area_sqm ?? "",
    perimeter_m: raw.perimeter_m ?? "",
    max_capacity: raw.max_capacity ?? "",
    event_type: raw.event_type ?? "",
    event_size: raw.event_size ?? "",
    svg_content: raw.svg_content ?? "",
    svg_viewbox: raw.svg_viewbox ?? "",
    description_html: raw.description_html ?? "",
    is_template: Boolean(raw.is_template),
    is_active: raw.is_active !== false,
    priority: raw.priority ?? "",
    active_from: raw.active_from ?? "",
    active_until: raw.active_until ?? "",
    location_name: raw.location_name ?? raw.location?.name ?? "—",
    created_by_tenant_id: raw.created_by_tenant_id ?? "",
    created_by_tenant_name: raw.created_by_tenant_name ?? "",
  };
};

const initialFormData = {
  location_id: "",
  tenant_id: "",
  name: "",
  scenario_code: "",
  description: "",
  reference_lat: "",
  reference_lng: "",
  area_sqm: "",
  perimeter_m: "",
  max_capacity: "",
  event_type: "",
  event_size: "",
  svg_content: "",
  svg_viewbox: "",
  viewbox_x: String(DEFAULT_VIEWBOX.x),
  viewbox_y: String(DEFAULT_VIEWBOX.y),
  viewbox_width: String(DEFAULT_VIEWBOX.width),
  viewbox_height: String(DEFAULT_VIEWBOX.height),
  description_html: "",
  is_template: false,
  is_active: true,
  priority: "",
  active_from: "",
  active_until: "",
};

const ScenarioManagement = () => {
  const navigate = useNavigate();
  const { userRoles } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);
  const canUseMapEditor = hasMapEditorAccess(userRoles || []);
  const [scenarios, setScenarios] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const autoInactiveApplied = useRef(false);
  useEffect(() => {
    if (isSuperAdmin && !autoInactiveApplied.current) {
      autoInactiveApplied.current = true;
      setIncludeInactive(true);
      setPage(1);
    }
  }, [isSuperAdmin]);

  const [loading, setLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const data = await locationService.getLocations({ page: 1, limit: 500 });
      const items = data?.items ?? data?.locations ?? data?.data ?? [];
      const normalized = (Array.isArray(items) ? items : [])
        .map((item) => ({
          id: Number(item.id ?? item.location_id) || 0,
          name: item.name ?? "—",
        }))
        .filter((item) => item.id > 0);
      setLocations(normalized);
    } catch (err) {
      console.error("Error loading locations:", err);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await tenantService.getAllTenants();
      const items = Array.isArray(data)
        ? data
        : (data?.items ?? data?.tenants ?? data?.data ?? []);
      const normalized = (Array.isArray(items) ? items : [])
        .map((t) => ({ id: t.tenant_id ?? t.id, name: t.name ?? "—" }))
        .filter((t) => t.id);
      setTenants(normalized);
    } catch (err) {
      console.error("Error loading tenants:", err);
      setTenants([]);
    }
  }, []);

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await scenarioService.getScenarios({
        page,
        limit,
        search: search.trim() || undefined,
        location_id:
          selectedLocationId !== "all" ? Number(selectedLocationId) : undefined,
        include_inactive: includeInactive || undefined,
      });

      const items = data?.items ?? data?.scenarios ?? data?.data ?? [];
      const totalCount =
        data?.total ?? data?.count ?? (Array.isArray(items) ? items.length : 0);

      setScenarios(
        (Array.isArray(items) ? items : [])
          .map(normalizeScenarioFromApi)
          .filter(Boolean),
      );
      setTotal(Number(totalCount) || 0);
    } catch (err) {
      setError(
        formatErrorMessage(err) || "Szenarien konnten nicht geladen werden.",
      );
      setScenarios([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, selectedLocationId, includeInactive]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  useEffect(() => {
    if (tenants.length === 1) {
      setFormData((p) =>
        p.tenant_id ? p : { ...p, tenant_id: tenants[0].id },
      );
    }
  }, [tenants]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total, limit],
  );

  const tenantOptions = useMemo(() => {
    const ownerId = editingScenario?.created_by_tenant_id;
    if (ownerId && !tenants.some((t) => String(t.id) === String(ownerId))) {
      return [
        {
          id: String(ownerId),
          name: editingScenario.created_by_tenant_name || "—",
        },
        ...tenants,
      ];
    }
    return tenants;
  }, [tenants, editingScenario]);

  const {
    sortedItems: sortedScenarios,
    sortConfig,
    requestSort,
  } = useSortableData(
    scenarios,
    SCENARIO_SORT_ACCESSORS,
    DEFAULT_SCENARIO_SORT,
  );

  const resetForm = () => {
    setFormData(initialFormData);
    setActionError(null);
  };

  const openCreate = () => {
    setEditingScenario(null);
    setActionError(null);
    setFormData({
      ...initialFormData,
      tenant_id: tenants.length === 1 ? tenants[0].id : "",
    });
    setShowCreateModal(true);
  };

  const openEdit = (scenario) => {
    setActionError(null);
    setEditingScenario(scenario);
    const viewBox =
      parseViewBoxString(scenario.svg_viewbox) ||
      parseViewBoxString(
        scenario.svg_content
          ? (() => {
              try {
                const doc = new DOMParser().parseFromString(
                  scenario.svg_content,
                  "image/svg+xml",
                );
                return doc.documentElement?.getAttribute("viewBox");
              } catch {
                return null;
              }
            })()
          : null,
      ) ||
      DEFAULT_VIEWBOX;
    setFormData({
      location_id: scenario.location_id ? String(scenario.location_id) : "",
      tenant_id: scenario.created_by_tenant_id
        ? String(scenario.created_by_tenant_id)
        : "",
      name: scenario.name ?? "",
      scenario_code: scenario.scenario_code ?? "",
      description: scenario.description ?? "",
      reference_lat:
        scenario.reference_lat != null ? String(scenario.reference_lat) : "",
      reference_lng:
        scenario.reference_lng != null ? String(scenario.reference_lng) : "",
      area_sqm: scenario.area_sqm != null ? String(scenario.area_sqm) : "",
      perimeter_m:
        scenario.perimeter_m != null ? String(scenario.perimeter_m) : "",
      max_capacity:
        scenario.max_capacity != null ? String(scenario.max_capacity) : "",
      event_type: scenario.event_type ?? "",
      event_size: scenario.event_size ?? "",
      svg_content: scenario.svg_content ?? "",
      svg_viewbox: scenario.svg_viewbox ?? "",
      viewbox_x: String(viewBox.x),
      viewbox_y: String(viewBox.y),
      viewbox_width: String(viewBox.width),
      viewbox_height: String(viewBox.height),
      description_html: scenario.description_html ?? "",
      is_template: Boolean(scenario.is_template),
      is_active: scenario.is_active !== false,
      priority: scenario.priority != null ? String(scenario.priority) : "",
      active_from: toDateInputValue(scenario.active_from),
      active_until: toDateInputValue(scenario.active_until),
    });
  };

  const buildPayload = (isCreate) => {
    const trimmedName = formData.name.trim();
    const locationId = Number(formData.location_id);
    const errors = [];

    if (isCreate && (!locationId || locationId <= 0)) {
      errors.push("Standort ist erforderlich.");
    }
    if (isCreate && !formData.tenant_id) {
      errors.push("Mandant ist erforderlich.");
    }
    if (isCreate && !trimmedName) {
      errors.push("Name ist erforderlich.");
    }
    if (formData.name.length > 255)
      errors.push("Name darf maximal 255 Zeichen lang sein.");
    if (formData.scenario_code.length > 63)
      errors.push("Scenario-Code darf maximal 63 Zeichen lang sein.");

    const numberFieldChecks = [
      { key: "area_sqm", min: 0, label: "Fläche (m²)" },
      { key: "perimeter_m", min: 0, label: "Umfang (m)" },
      { key: "max_capacity", min: 0, label: "Max. Kapazität" },
      { key: "viewbox_width", min: 1, label: "ViewBox Breite" },
      { key: "viewbox_height", min: 1, label: "ViewBox Höhe" },
    ];

    numberFieldChecks.forEach(({ key, min, label }) => {
      const value = formData[key];
      if (value !== "" && Number(value) < min) {
        errors.push(`${label} muss >= ${min} sein.`);
      }
    });

    const activeFromIso = toIsoFromDateInput(formData.active_from);
    const activeUntilIso = toIsoFromDateInput(formData.active_until, true);
    if (
      activeFromIso &&
      activeUntilIso &&
      new Date(activeFromIso) > new Date(activeUntilIso)
    ) {
      errors.push("„Aktiv von“ muss vor „Aktiv bis“ liegen.");
    }

    if (errors.length > 0) {
      return { payload: null, errors };
    }

    const viewBox = parseViewBoxString({
      x: formData.viewbox_x,
      y: formData.viewbox_y,
      width: formData.viewbox_width,
      height: formData.viewbox_height,
    });
    const svg_viewbox = viewBox ? formatViewBoxString(viewBox) : null;

    const payload = {
      ...(formData.location_id ? { location_id: locationId } : {}),
      ...(isCreate && formData.tenant_id
        ? { tenant_id: formData.tenant_id }
        : {}),
      ...(trimmedName ? { name: trimmedName } : {}),
      ...(formData.scenario_code.trim()
        ? { scenario_code: formData.scenario_code.trim() }
        : {}),
      ...(formData.description.trim()
        ? { description: formData.description.trim() }
        : {}),
      ...(formData.reference_lat !== ""
        ? { reference_lat: Number(formData.reference_lat) }
        : {}),
      ...(formData.reference_lng !== ""
        ? { reference_lng: Number(formData.reference_lng) }
        : {}),
      ...(formData.area_sqm !== ""
        ? { area_sqm: Number(formData.area_sqm) }
        : {}),
      ...(formData.perimeter_m !== ""
        ? { perimeter_m: Number(formData.perimeter_m) }
        : {}),
      ...(formData.max_capacity !== ""
        ? { max_capacity: Number(formData.max_capacity) }
        : {}),
      ...(formData.event_type ? { event_type: formData.event_type } : {}),
      ...(formData.event_size ? { event_size: formData.event_size } : {}),
      ...(svg_viewbox ? { svg_viewbox } : {}),
      ...(formData.description_html.trim()
        ? { description_html: formData.description_html }
        : {}),
      is_template: Boolean(formData.is_template),
      is_active: Boolean(formData.is_active),
      ...(formData.priority !== ""
        ? { priority: Number(formData.priority) }
        : {}),
      ...(activeFromIso ? { active_from: activeFromIso } : {}),
      ...(activeUntilIso ? { active_until: activeUntilIso } : {}),
    };

    return { payload, errors: [] };
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const { payload, errors } = buildPayload(true);
    if (errors.length > 0 || !payload) {
      setActionError(errors.join(" "));
      return;
    }

    setSaving(true);
    setActionError(null);
    try {
      const created = await scenarioService.createScenario(payload);
      setShowCreateModal(false);
      resetForm();
      const newId = created?.id ?? created?.scenario_id ?? created?.data?.id;
      if (canUseMapEditor && newId != null) {
        navigate(`/admin/scenario-editor/${newId}`);
        return;
      }
      await fetchScenarios();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingScenario) return;

    const { payload, errors } = buildPayload(false);
    if (errors.length > 0 || !payload) {
      setActionError(errors.join(" "));
      return;
    }

    setSaving(true);
    setActionError(null);
    try {
      await scenarioService.updateScenario(editingScenario.id, payload);
      setEditingScenario(null);
      resetForm();
      await fetchScenarios();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setActionError(null);
    try {
      await scenarioService.deleteScenario(deleteConfirm.id);
      setDeleteConfirm(null);
      await fetchScenarios();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <GlobalAdminHeader
        title="Szenario Verwaltung"
        subtitle="Szenarien pro Standort erstellen und bearbeiten"
        buttons={[
          {
            text: "Neues Szenario",
            icon: <Plus className="h-4 w-4" />,
            onClick: openCreate,
            className:
              "flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Szenarien durchsuchen..."
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
          />
          <select
            value={selectedLocationId}
            onChange={(e) => {
              setSelectedLocationId(e.target.value);
              setPage(1);
            }}
            disabled={locationsLoading}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
          >
            <option value="all">Alle Standorte</option>
            {locations.map((loc) => (
              <option key={loc.id} value={String(loc.id)}>
                {loc.name}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => {
                setIncludeInactive(e.target.checked);
                setPage(1);
              }}
            />
            Inaktive anzeigen
          </label>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Standort"
                    sortKey="location_name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Mandant"
                    sortKey="created_by_tenant_name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Veranstaltungstyp"
                    sortKey="event_type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Größe"
                    sortKey="event_size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Aktiv von/bis"
                    sortKey="active_period"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      <PageLoader message="Lade Szenarien..." />
                    </td>
                  </tr>
                ) : scenarios.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      Keine Szenarien gefunden.
                    </td>
                  </tr>
                ) : (
                  sortedScenarios.map((scenario) => (
                    <tr
                      key={scenario.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-slate-100">
                        {scenario.name || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {scenario.location_name || scenario.location_id || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {scenario.created_by_tenant_name || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {scenario.event_type
                          ? EVENT_TYPE_MAP[scenario.event_type] ||
                            scenario.event_type
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {scenario.event_size
                          ? EVENT_SIZE_MAP[scenario.event_size] ||
                            scenario.event_size
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 whitespace-nowrap">
                        {scenario.active_from || scenario.active_until
                          ? `${toDateInputValue(scenario.active_from) || "…"} – ${
                              toDateInputValue(scenario.active_until) || "…"
                            }`
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const windowState = scenario.is_active
                            ? getScenarioWindowState(scenario)
                            : "inactive";
                          const badge = {
                            inactive: {
                              label: "Inaktiv",
                              cls: "bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-300",
                            },
                            expired: {
                              label: "Abgelaufen",
                              cls: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200",
                            },
                            scheduled: {
                              label: "Geplant",
                              cls: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200",
                            },
                            live: {
                              label: "Aktiv",
                              cls: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200",
                            },
                          }[windowState];
                          return (
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canUseMapEditor && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/admin/scenario-editor/${scenario.id}`,
                                )
                              }
                              className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors"
                              title="Im Map Editor öffnen"
                            >
                              <MapIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(scenario)}
                            className="p-2 rounded-lg hover:bg-primary-color-tint primary-color transition-colors"
                            title="Bearbeiten"
                          >
                            <SquarePen className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(scenario)}
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 text-sm">
              <span className="text-gray-600 dark:text-slate-400">
                {total} Szenario{total !== 1 ? "s" : ""}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 disabled:opacity-50"
                >
                  ‹
                </button>
                <span className="text-gray-700 dark:text-slate-300">
                  Seite {page} von {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 disabled:opacity-50"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(showCreateModal || editingScenario) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() =>
              !saving &&
              (showCreateModal
                ? setShowCreateModal(false)
                : setEditingScenario(null))
            }
            aria-hidden
          />
          <div
            className="relative w-full max-w-3xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {editingScenario ? "Szenario bearbeiten" : "Neues Szenario"}
              </h2>
              <div className="flex items-center gap-2">
                {editingScenario && canUseMapEditor && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/admin/scenario-editor/${editingScenario.id}`)
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    title="Dieses Szenario im Map Editor öffnen"
                  >
                    <MapIcon className="w-4 h-4" />
                    Im Map Editor öffnen
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    showCreateModal
                      ? setShowCreateModal(false)
                      : setEditingScenario(null)
                  }
                  disabled={saving}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-slate-400 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <form
              onSubmit={editingScenario ? handleEditSubmit : handleCreateSubmit}
              className="p-6 space-y-4 max-h-[80vh] overflow-y-auto"
            >
              {actionError && (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {actionError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Mandant *
                </label>
                {/* Ownership can't be changed once set; tenant admins have a
                    single (locked) option. */}
                <select
                  value={formData.tenant_id}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, tenant_id: e.target.value }))
                  }
                  disabled={Boolean(editingScenario) || tenants.length === 1}
                  required={!editingScenario}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">— Auswählen —</option>
                  {tenantOptions.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Standort *
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        location_id: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    required={!editingScenario}
                  >
                    <option value="">— Auswählen —</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={String(loc.id)}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    maxLength={255}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    required={!editingScenario}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Scenario-Code
                  </label>
                  <input
                    type="text"
                    maxLength={63}
                    value={formData.scenario_code}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        scenario_code: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Priorität
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, priority: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung
                </label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  SVG ViewBox
                </label>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
                  Canvas size for the map editor (stored as{" "}
                  <code className="text-xs">svg_viewbox</code> in the database).
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                      X Offset
                    </label>
                    <input
                      type="number"
                      value={formData.viewbox_x}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          viewbox_x: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                      Y Offset
                    </label>
                    <input
                      type="number"
                      value={formData.viewbox_y}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          viewbox_y: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                      Width
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={formData.viewbox_width}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          viewbox_width: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                      Height
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={formData.viewbox_height}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          viewbox_height: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Lat
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={formData.reference_lat}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        reference_lat: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Lng
                  </label>
                  <input
                    type="number"
                    step="0.00000001"
                    value={formData.reference_lng}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        reference_lng: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Max. Kapazität
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.max_capacity}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        max_capacity: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Event-Typ
                  </label>
                  <select
                    value={formData.event_type}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, event_type: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  >
                    <option value="">—</option>
                    {Object.entries(EVENT_TYPE_MAP).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Event-Größe
                  </label>
                  <select
                    value={formData.event_size}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, event_size: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  >
                    <option value="">—</option>
                    {Object.entries(EVENT_SIZE_MAP).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Fläche (m²)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.area_sqm}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, area_sqm: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Umfang (m)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={formData.perimeter_m}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        perimeter_m: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Karte
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  {formData.svg_content ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-300">
                      ✓ Karte vorhanden
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300">
                      ⚠ Noch keine Karte
                    </span>
                  )}
                  {canUseMapEditor &&
                    (editingScenario ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/admin/scenario-editor/${editingScenario.id}`,
                          )
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                      >
                        <MapIcon className="w-4 h-4" />
                        Karte im Map Editor bearbeiten
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        Nach dem Speichern im Map Editor hinzufügen.
                      </span>
                    ))}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                  Hintergrundkarte und buchbare Flächen werden im Map Editor
                  verwaltet und als SVG gespeichert.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Beschreibung (HTML)
                </label>
                <textarea
                  rows={4}
                  value={formData.description_html}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      description_html: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Aktiv von
                  </label>
                  <input
                    type="date"
                    value={formData.active_from}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        active_from: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Aktiv bis
                  </label>
                  <input
                    type="date"
                    value={formData.active_until}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        active_until: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm">Aktiv</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_template}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        is_template: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm">Template</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() =>
                    showCreateModal
                      ? setShowCreateModal(false)
                      : setEditingScenario(null)
                  }
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingScenario ? "Speichern" : "Anlegen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setDeleteConfirm(null)}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:border dark:border-slate-700 max-w-md w-full p-6">
            {actionError && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {actionError}
              </div>
            )}
            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
              Szenario löschen?
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
              &quot;{deleteConfirm.name ?? "Unbekannt"}&quot; wird dauerhaft
              gelöscht.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 dark:bg-red-500 text-white font-medium hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioManagement;
